import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings -> Authentication -> "Provisioning (SCIM)".
 *
 *   GET  /settings/authentication/scim                     ecranul
 *   POST /settings/authentication/scim                     setarile
 *   POST /settings/authentication/scim/tokens              jeton nou - `token` vine O SINGURA DATA
 *   POST /settings/authentication/scim/tokens/{id}/revoke  revocare
 *
 * Lista de jetoane nu poarta niciodata jetonul, numai inceputul lui (`prefix`).
 */

export type ScimSettings = {
  enabled: boolean
  providerId: number
  linkAtLogin: boolean
  companyId: number
  officeId: number
  unmappedGroups: 'ignore' | 'reject' | string
  ipAllowlist: string
  mtlsTrust: boolean
  mtlsHeader: string
  mtlsFingerprints: string
  disableJit: boolean
  updatedAt: string
}

export type ScimToken = {
  id: number
  name: string
  prefix: string
  createdAt: string
  expiresAt: string
  revokedAt: string
  lastUsedAt: string
  lastUsedIp: string
  state: 'active' | 'expired' | 'revoked' | string
}

export type ScimRule = {
  id: number
  group: string
  role: string
  allCompanies: boolean
  companyIds: number[]
  active: boolean
}

export type ScimCall = {
  id: number
  when: string
  token: string
  method: string
  operation: string
  target: string
  status: number
  detail: string
  from: string
}

export type ScimPage = {
  /** Semnul ca raspunsul e chiar al ecranului SCIM (un server mai vechi nu-l are). */
  scim: true
  canWrite: boolean
  baseUrl: string
  /** Adresa API-ului REST de provizionare (acelasi jeton); un server mai vechi n-o trimite. */
  restUrl?: string
  settings: ScimSettings
  providers: Array<{ id: number; name: string; protocol: string; provisioning: number }>
  companies: Array<{ id: number; name: string }>
  tokens: ScimToken[]
  rules: ScimRule[]
  status: { lastSuccess: string; errors24h: number; calls24h: number }
  calls: ScimCall[]
}

export type ScimWriteResult = { status: string; message: string; token?: string; id?: number }

export const scimQuery = queryOptions({
  queryKey: ['settings', 'authentication', 'scim'],
  queryFn: () => apiGet<ScimPage>('/settings/authentication/scim'),
  retry: false,
})

export function saveScimSettings(fields: {
  enabled: boolean
  provider_id: number
  link_at_login: boolean
  company_id: number
  office_id: number
  unmapped_groups: string
  ip_allowlist: string
  mtls_trust: boolean
  mtls_header: string
  mtls_fingerprints: string
  disable_jit: boolean
}) {
  return apiPost<ScimWriteResult>('/settings/authentication/scim', {
    ...fields,
    enabled: fields.enabled ? 1 : 0,
    link_at_login: fields.link_at_login ? 1 : 0,
    mtls_trust: fields.mtls_trust ? 1 : 0,
    disable_jit: fields.disable_jit ? 1 : 0,
  })
}

export function createScimToken(name: string, expiresAt: string) {
  return apiPost<ScimWriteResult>('/settings/authentication/scim/tokens', { name, expires_at: expiresAt })
}

export function revokeScimToken(id: number) {
  return apiPost<ScimWriteResult>(`/settings/authentication/scim/tokens/${id}/revoke`, {})
}

/** Pagina e chiar a ecranului SCIM - nu altceva, intors de un server mai vechi sau de o proba. */
export function isScimPage(value: unknown): value is ScimPage {
  return typeof value === 'object' && value !== null && (value as { scim?: unknown }).scim === true
}
